import React, { useCallback, useMemo } from 'react'

import moment from 'moment/moment'

import map from 'lodash/map'

import {
  Divider,
  Grid,
  Typography
} from '@material-ui/core'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import { edit } from '@galo-monitor/icons'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import colors from '@galo-monitor/styles/colors'
import { momentFriendlyDateTimeFormat } from '@galo-monitor/utils/dates'
import Card from '@galo-monitor/web-components/Card'
import Icon from '@galo-monitor/web-components/Icon'
import IconButton from '@galo-monitor/web-components/IconButton'
import { CircularLoader } from '@galo-monitor/web-components/Loader/Loader'
import Screen from '@galo-monitor/web-components/Screen'
import ChangePasswordModal from '@galo-monitor/web-containers/modals/common/ChangePasswordModal'
import UserProfileModal from '@galo-monitor/web-containers/modals/common/UserProfileModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'


import useStyles from './styles'
import {
  CardTitle,
  Container,
  GridContainer,
  GridProfileDetails,
  GridPersonalInformation,
  GridPassword,
  UserProfilePicture
} from './theme'

const LANGUAGE_LABELS = {
  'pt-BR': 'Português (pt-BR)',
  'en-US': 'English',
  'de-DE': 'Deutsch'
}

export default function UserProfile() {
  const classes = useStyles()
  const t = useT()
  const { routes } = useRoutes()
  const { createDialog } = useDialog()

  const { data: currentUserData, error, mutate } = useGaloMonitorApi('/shared/users')

  const cardsProps = useMemo(() => ({
    cardStyle: { width: '100%' },
    cardContentProps: {
      style: { padding: '5px 30px' }
    },
    headerTitleProps: {
      style: {
        flex: 0,
        marginRight: 5,
        whiteSpace: 'nowrap'
      }
    },
    style: {
      display: 'block',
      margin: 0
    }
  }), [])

  const userInfo = useMemo(() => ({
    name: currentUserData?.name,
    email: currentUserData?.email,
    phone: currentUserData?.phone,
    language: LANGUAGE_LABELS[currentUserData?.language]
  }), [currentUserData])

  const userSecurityInfo = useMemo(() => ({
    password: `${ t('updated at') } ${ moment(currentUserData?.updateAtPassword).format(momentFriendlyDateTimeFormat) }`
  }), [currentUserData, t])

  const crumbs = useMemo(() => [
    {
      title: routes.userProfile.title,
      key: 'user-profile-screen',
      url: '/user/profile',
      active: true
    }
  ], [routes.userProfile.title])

  const isLoadingUserData = useMemo(() => !currentUserData && !error, [currentUserData, error])

  const languageOptions = useMemo(
    () => map(
      Object.entries(LANGUAGE_LABELS),
      ([key, value]) => ({ label: value, value: key })
    ),
    []
  )

  const handleEditProfileDetails = useCallback(() => {
    createDialog({
      id: 'user-profile-modal',
      Component: UserProfileModal,
      props: {
        user: currentUserData,
        languageOptions,
        onSuccess: mutate
      }
    })
  }, [createDialog, currentUserData, languageOptions, mutate])

  const handleChangePassword = useCallback(() => {
    createDialog({
      id: 'user-change-password-modal',
      Component: ChangePasswordModal,
      props: {
        onSuccess: mutate
      }
    })
  }, [createDialog, mutate])

  return isLoadingUserData ? (
    <CircularLoader
      marginTop="30vh"
      fullSpace
    />
  ) : (
    <Screen title={ routes.userProfile.title } breadcrumbs={ crumbs }>
      <GridContainer className={ classes.root }>
        <GridProfileDetails className={ classes.profile }>
          <Card
            { ...cardsProps }
            title={ <CardTitle variant="body2">{ t('profile details') }</CardTitle> }
          >
            <Container>
              <UserProfilePicture className={ classes.profileAvatar }>
                <CardTitle variant="h3" color="textPrimary" >
                  { userInfo?.name?.[0] }
                </CardTitle>
              </UserProfilePicture>
              <Container>
                <CardTitle color="primary">{ userInfo.name }</CardTitle>
                {currentUserData?.positionRole ? (
                  <Typography
                    color="textSecondary"
                    variant="body2"
                    gutterBottom
                  >
                    { currentUserData.positionRole }
                  </Typography>
                ) : null}
              </Container>
            </Container>
          </Card>
        </GridProfileDetails>
        <GridPersonalInformation>
          <Card
            { ...cardsProps }
            title={ <CardTitle variant="body2">{ t('personal information', { howMany: 2 }) }</CardTitle> }
            headerRight={
              <IconButton size="small" onClick={ handleEditProfileDetails }>
                <Icon size={ 15 } icon={ edit } color={ colors.darkGrey } />
              </IconButton>
            }
          >
            <Grid
              aria-label="user-info"
              direction="column"
              container
            >
              <Divider />
              {map(Object.entries(userInfo), ([key, value]) => (
                <React.Fragment key={ key }>
                  <Grid item container xs={ 12 }>
                    <Grid item xs={ 12 } sm={ 6 }>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                      >
                        {t(key)}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={ 12 }
                      sm={ 6 }
                    >
                      <Typography variant="body2">
                        {value || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider />
                </React.Fragment>
              ))}
            </Grid>
          </Card>
        </GridPersonalInformation>
        <GridPassword>
          <Card
            { ...cardsProps }
            title={ <CardTitle variant="body2">{ t('password') }</CardTitle> }
            headerRight={
              <IconButton size="small" onClick={ handleChangePassword }>
                <Icon size={ 15 } icon={ edit } color={ colors.darkGrey } />
              </IconButton>
            }
          >
            <Grid
              aria-label="user-info"
              direction="column"
              container
            >
              <Divider />
              {map(Object.entries(userSecurityInfo), ([key, value]) => (
                <React.Fragment key={ key }>
                  <Grid item container xs={ 12 }>
                    <Grid item xs={ 12 } sm={ 6 }>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                      >
                        {t(key)}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={ 12 }
                      sm={ 6 }
                    >
                      <Typography variant="body2">
                        {value}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider />
                </React.Fragment>
              ))}
            </Grid>
          </Card>
        </GridPassword>
      </GridContainer>
    </Screen>
  )
}
